import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { UnsavedChanges } from '@deltek/specification-client/core/Constants/messages'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import NavPrompt, {
    NavPromptMessageProps,
} from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { FacetValueToCount } from '@deltek/specification-client/core/Models/facet'
import {
    Firm,
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { FormPropsObject } from '@deltek/specification-client/core/Models/formProps'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProfileImageInput } from '@deltek/specification-client/core/Models/profileImageInput'
import { UpdateFirmDisplayInfoInput } from '@deltek/specification-client/core/Models/updateFirmDisplayInfoInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import CompanyBanner from '@deltek/specification-client/core/Widgets/Company/CompanyBanner'
import CompanyContactInfo from '@deltek/specification-client/core/Widgets/Company/CompanyContactInfo'
import CompanyLogo from '@deltek/specification-client/core/Widgets/Company/CompanyLogo'
import ListingCategoriesWidget from '@deltek/specification-client/core/Widgets/Company/ListingCategoriesWidget'
import ListingTypeWidget, {
    ListingTypeWidgetInfoBubbleProps,
} from '@deltek/specification-client/core/Widgets/Company/ListingTypeWidget'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { UpdateFirmDisplayInfoPageMutation } from 'api/graphQL/mutations/firm.mutations'
import { FirmProfileInfoQuery } from 'bpm/api/graphQL/queries/firm.queries'
import { ProductListingsQuery } from 'bpm/api/graphQL/queries/product.queries'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductListingsResponse } from 'models/product'
import React, { useState } from 'react'
import { Redirect, useLocation, useParams } from 'react-router-dom'
import ProductHeader from '../components/ProductHeader'
import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
    isUserBPMFirmAdminOrDeltekAdmin,
} from '../productsUtils'
import CompanyBannerEdit from './components/CompanyBannerEdit'
import CompanyLogoEdit from './components/CompanyLogoEdit'
import EditModeButtons from './components/EditModeButtons'
import EditableText from './components/EditableText'
import EditableTextBlock from './components/EditableTextBlock'

const useStyles = makeStyles((theme: Theme) => ({
    content: {
        marginTop: 16,
    },
    page: {},
    fullWidth: {
        width: '100%',
    },
    dividerContainer: {
        marginTop: theme.spacing(2),
    },
    thickDivider: {
        height: 6,
        backgroundColor: '#f2f2f2',
    },
    mapText: {
        fontWeight: 500,
    },
    leftColumnContainer: {
        flexWrap: 'nowrap',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    noRightPadding: {
        '&.MuiGrid-item': {
            paddingRight: 0,
        },
    },
    limitedHeight: {
        minHeight: 542,
        height: 'calc(100vh - 330px)',
    },
    textBlocks: {
        bottom: 0,
        flexWrap: 'nowrap',
        overflowY: 'auto',
        minHeight: 200,
        height: 'calc(100%)',
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
    categoryBlocks: {
        bottom: 0,
        flexWrap: 'nowrap',
        overflowY: 'auto',
        minHeight: 100,
    },
}))

const initialTextFieldContents: FormPropsObject = {
    displayName: { value: '', required: true },
    whoWeAre: { value: '', required: false },
    whyChooseUs: { value: '', required: false },
}

const dummyInfoBubble: ListingTypeWidgetInfoBubbleProps = {
    title: 'Title',
    content: 'Info bubble with quick explanation of heading meaning.',
}
const listingTypes =
    processEnv.REACT_APP_EnableBadgelessCards !== '1'
        ? [
            'Premium MasterSpec',
            'Premium',
            'Premium Proprietary',
            'Manufacturer',
        ]
        : ['Product Listing', 'Manufacturer']
const MAX_WORD_LIMIT = 10000
const headerMinHeight = 6.5
const CompanyPage: React.FC = () => {
    const classes = useStyles()
    const location = useLocation()
    const theme = useTheme()
    const mdDown = useMediaQuery(theme.breakpoints.down('sm'))
    const { firmId } = useParams<{ firmId?: string }>()
    const [isEditMode, setIsEditMode] = React.useState(false)
    const [isFormDirty, setIsFormDirty] = React.useState(false)
    const [categories, setCategories] = React.useState<FacetValueToCount[]>([])
    const [baseTextFieldContents, setBaseTextFieldContents] = React.useState(
        initialTextFieldContents
    )
    const [textFieldContents, setTextFieldContents] = React.useState(
        initialTextFieldContents
    )
    const [
        bannerImage,
        setBannerImage,
    ] = React.useState<ProfileImageInput | null>()
    const [logoImage, setLogoImage] = React.useState<ProfileImageInput | null>()
    const { updateAppHeaderContent, updateAppHeaderStyles } = React.useContext(
        AppHeaderContext
    )
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const [
        companyBannerFileInTransit,
        setCompanyBannerFileInTransit,
    ] = React.useState<boolean>(false)
    const [
        companyLogoFileInTransit,
        setCompanyLogoFileInTransit,
    ] = React.useState<boolean>(false)

    const { isDeltekAdmin } = isDeltekUserorAdmin(currentUserInfoResponse)

    const {
        loading: profileInfoLoading,
        error: profileInfoError,
        data: profileInfoData,
    } = useQuery<FirmResponse, FirmId>(FirmProfileInfoQuery, {
        variables: {
            // if firmId is present in query string, use that. if not use current users account firm id
            firmId:
                firmId ||
                (currentUserInfoResponse?.currentUserInfo.account.accountId ??
                    ''),
        },
    })

    const [
        updateFirmDisplayInfo,
        { loading: updateFirmDisplayInfoLoading },
    ] = useMutation<
        { updateFirmDisplayInfo: Partial<Firm> },
        { updateFirmDisplayInfoInput: Partial<UpdateFirmDisplayInfoInput> }
    >(UpdateFirmDisplayInfoPageMutation)
    const [filestackLoading, setFilestackLoading] = useState<boolean>(false)

    const [
        getListings,
        {
            loading: listingDataLoading,
            error: listingDataError,
            data: listingData,
        },
    ] = useLazyQuery<ProductListingsResponse>(ProductListingsQuery, {
        fetchPolicy: 'no-cache',
    })

    const showErrorSnackbar = React.useCallback(() => {
        showGlobalSnackbar({
            variables: {
                message:
                    'An error occured while trying to save changes. Please try again.',
                variant: 'error',
                id: 'CompanyPage_profileUpdateError',
            },
        })
    }, [showGlobalSnackbar])

    const editModeButtonsContainerRef = React.useRef<HTMLDivElement>(null)

    /**
     * Transfers loaded data into the page's
     * editable form field states.
     */
    React.useEffect(() => {
        if (profileInfoData && profileInfoData.firm) {
            if (!listingData)
                getListings({
                    variables: {
                        listingSearchInput: {
                            facets: [
                                {
                                    name: 'displayNameWithFirmName',
                                    values: [
                                        `'${profileInfoData?.firm?.displayName}'%|%'${profileInfoData?.firm?.name}'`,
                                    ],
                                },
                                {
                                    name: 'isActive',
                                    values: ['true'],
                                },
                            ],
                        },
                    },
                })
            const fieldContents = {
                whoWeAre: {
                    required: false,
                    value: profileInfoData?.firm?.profileInfo?.whoWeAre ?? '',
                    valid: true,
                },
                whyChooseUs: {
                    required: false,
                    valid: true,
                    value:
                        profileInfoData?.firm?.profileInfo?.whyChooseUs ?? '',
                },
                displayName: {
                    required: true,
                    valid: !!profileInfoData?.firm?.displayName,
                    value: profileInfoData.firm?.displayName ?? '',
                },
            }
            setBaseTextFieldContents(fieldContents)
            setTextFieldContents(fieldContents)
        }
    }, [profileInfoData, getListings, listingData])

    React.useEffect(() => {
        if (listingData) {
            setCategories(
                listingData?.listingSearch.facets?.find(
                    (facet) => facet.name === 'Categories'
                )?.values || []
            )
        }
    }, [listingData])

    const getListingLevelCount = React.useCallback(
        (listingLevelName: string) => {
            if (listingData) {
                return (
                    listingData?.listingSearch?.facets
                        ?.find((facet) => facet.name === 'ListingLevels')
                        ?.values.find(
                            (facetItem) => facetItem.value === listingLevelName
                        )?.count ?? 0
                )
            } else {
                return 0
            }
        },
        [listingData]
    )

    const handleCancel = React.useCallback(() => {
        if (isFormDirty) {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Company Page',
                    preferredMessage: UnsavedChanges,
                    buttonColor: '#2D9CDB',
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (!callbackState) return
                    setTextFieldContents(baseTextFieldContents)
                    setIsEditMode(false)
                }
            )
        } else {
            setIsEditMode(false)
        }
    }, [baseTextFieldContents, isFormDirty])

    const handleSave = React.useCallback(
        (exitAfter: boolean = false) => {
            const hasErrors =
                Object.keys(textFieldContents).find(
                    (key) => !textFieldContents[key].valid
                ) !== undefined

            if (!hasErrors) {
                let updatedFirmProfileObject = {
                    firmId:
                        firmId ||
                        (currentUserInfoResponse?.currentUserInfo.account
                            .accountId ??
                            ''),
                    profileInfo: {
                        whoWeAre: textFieldContents.whoWeAre.value ?? '',
                        whyChooseUs: textFieldContents.whyChooseUs.value ?? '',
                    },
                } as UpdateFirmDisplayInfoInput
                if (isDeltekAdmin) {
                    updatedFirmProfileObject.displayName =
                        textFieldContents.displayName.value ?? ''
                }
                if (bannerImage) {
                    updatedFirmProfileObject.profileInfo.bannerImage = bannerImage
                }
                if (logoImage) {
                    updatedFirmProfileObject.profileInfo.logoImage = logoImage
                }
                updateFirmDisplayInfo({
                    variables: {
                        updateFirmDisplayInfoInput: updatedFirmProfileObject,
                    },
                })
                    .then(() => {
                        showGlobalSnackbar({
                            variables: {
                                message: 'Saved successfully',
                                variant: 'success',
                                id: 'CompanyPage_profileUpdateSuccess',
                            },
                        })
                        setBaseTextFieldContents(textFieldContents)
                        setBannerImage(null)
                        setLogoImage(null)
                        setIsFormDirty(false)

                        if (exitAfter) setIsEditMode(false)
                    })
                    .catch(() => {
                        showErrorSnackbar()
                    })
            }
        },
        [
            currentUserInfoResponse,
            textFieldContents,
            bannerImage,
            showGlobalSnackbar,
            showErrorSnackbar,
            updateFirmDisplayInfo,
            logoImage,
            firmId,
            isDeltekAdmin,
        ]
    )

    const handleBannerImageChange = React.useCallback(
        (newValue: ProfileImageInput) => {
            setBannerImage(newValue)
            setIsFormDirty(true)
        },
        []
    )

    const handleLogoImageChange = React.useCallback(
        (newValue: ProfileImageInput) => {
            setLogoImage(newValue)
            setIsFormDirty(true)
        },
        []
    )

    const handleCompanyBannerFileInTransitChange = (inTransit: boolean) => {
        if (inTransit) {
            setIsFormDirty(true)
        }
        setCompanyBannerFileInTransit(inTransit)
    }

    const handleCompanyLogoFileInTransitChange = (inTransit: boolean) => {
        if (inTransit) {
            setIsFormDirty(true)
        }
        setCompanyLogoFileInTransit(inTransit)
    }

    /**
     * Update dirty/pristine state of the page in edit mode.
     */
    React.useEffect(() => {
        const newFormDirty =
            Object.keys(textFieldContents).find(
                (key) =>
                    textFieldContents[key].value !==
                    baseTextFieldContents[key].value
            ) !== undefined
        setIsFormDirty(newFormDirty)
    }, [textFieldContents, baseTextFieldContents])

    /**
     * Updates App Header based on
     * edit mode state.
     */
    React.useEffect(() => {
        if (isEditMode) {
            updateAppHeaderStyles?.({
                titleTextColor: '#ffffff',
                backgroundColor: '#0069AA',
            })

            updateAppHeaderContent(
                'Company Page (Edit Mode)',
                <EditModeButtons
                    editModeButtonsContainerRef={editModeButtonsContainerRef}
                    onSave={() => handleSave(false)}
                    onCancel={handleCancel}
                    onSaveAndExit={() => handleSave(true)}
                    saveButtonLoading={
                        updateFirmDisplayInfoLoading ||
                        companyBannerFileInTransit ||
                        companyLogoFileInTransit
                    }
                    saveAndExitButtonLoading={
                        updateFirmDisplayInfoLoading ||
                        companyBannerFileInTransit ||
                        companyLogoFileInTransit
                    }
                    disabled={!textFieldContents.whoWeAre.valid}
                />
            )
        } else {
            if (currentUserInfoResponse) {
                const handleEditModeButtonClick = () => {
                    setIsEditMode(true)
                }

                const pageIsEditable = isUserBPMFirmAdminOrDeltekAdmin({
                    currentUserInfo: currentUserInfoResponse.currentUserInfo,
                    firmId: !firmId
                        ? currentUserInfoResponse.currentUserInfo.account
                            .accountId
                        : firmId,
                })

                updateAppHeaderContent(
                    'Company Page',
                    <ProductHeader
                        onEditModeClick={handleEditModeButtonClick}
                        loading={profileInfoLoading || !!profileInfoError}
                        showEditModeButton={pageIsEditable}
                    />
                )
                updateAppHeaderStyles?.({
                    titleTextColor: '#04325B',
                    backgroundColor: '#ffffff',
                })
            }
        }
    }, [
        firmId,
        isEditMode,
        handleSave,
        handleCancel,
        updateAppHeaderContent,
        updateAppHeaderStyles,
        companyBannerFileInTransit,
        companyLogoFileInTransit,
        currentUserInfoResponse,
        profileInfoError,
        profileInfoLoading,
        updateFirmDisplayInfoLoading,
    ])

    React.useEffect(() => {
        if (!isEditMode) {
            setIsFormDirty(false)
            setBannerImage(null)
            setLogoImage(null)
        } else {
            setTimeout(() => {
                editModeButtonsContainerRef.current
                    ?.querySelector('button')
                    ?.focus()
            }, 0)
        }
    }, [isEditMode])

    const handleTextFieldContentChange = (
        newContent: string,
        textBlockPropName: string
    ) => {
        setTextFieldContents({
            ...textFieldContents,
            [textBlockPropName]: {
                ...textFieldContents[textBlockPropName],
                valid:
                    textBlockPropName === 'whoWeAre'
                        ? newContent.length <= MAX_WORD_LIMIT
                        : textFieldContents[textBlockPropName].valid,
                value: newContent,
            },
        })
    }

    const handleDisplayNameChange = (newContent: string) => {
        const isValid = newContent.trim().length > 0
        setTextFieldContents({
            ...textFieldContents,
            displayName: {
                valid: isValid,
                value: newContent,
                required: true,
            },
        })
    }

    /**
     * This is a workaround for the filestack
     * "onOpen" delay. It starts a poll for the presence of
     * the filestack picker modal in the DOM, and sets
     * the filestackLoading state to false once the
     * element is found.
     */
    const checkPickerUi = () => {
        const hasPickerUi =
            document.getElementsByClassName('fsp-modal').length >= 1
        if (hasPickerUi) {
            setFilestackLoading(false)
        } else {
            setTimeout(() => checkPickerUi(), 500)
        }
    }

    const handleFilestackLoading = (isLoading: boolean | null) => {
        if (isLoading === false) {
            // Cover the "gap" by starting a poll
            // for the picker modal element in the DOM.
            checkPickerUi()
        } else {
            setFilestackLoading(true)
        }
    }

    const handleEscKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape' && isEditMode) {
                e.stopPropagation()
                handleCancel()
            }
        },
        [isEditMode, handleCancel]
    )

    if (profileInfoData && !profileInfoData.firm) {
        return (
            <Redirect
                to={{
                    pathname: location.pathname,
                    state: { notFound: true },
                }}
            />
        )
    }
    if (!(firmId || firmId?.length)) {
        if (
            !isDeltekUserorAdmin(currentUserInfoResponse).isDeltekUser &&
            !isUserBPMAdmin(currentUserInfoResponse).isBPM
        ) {
            return (
                <Redirect
                    to={{
                        pathname: location.pathname,
                        state: { notFound: true },
                    }}
                />
            )
        }
    }

    const chunkArray = (arr: string[], size: number) => {
        const chunks = []
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size))
        }
        return chunks
    }

    return (
        <div onKeyDown={handleEscKeyDown}>
            {isEditMode ? (
                <CompanyBannerEdit
                    handleCompanyBannerChange={handleBannerImageChange}
                    bannerUrl={profileInfoData?.firm.profileInfo?.bannerUrl}
                    companyBannerFileInTransit={companyBannerFileInTransit}
                    handleCompanyBannerFileInTransit={
                        handleCompanyBannerFileInTransitChange
                    }
                    handleFilestackLoading={handleFilestackLoading}
                />
            ) : (
                <CompanyBanner
                    bannerUrl={profileInfoData?.firm?.profileInfo?.bannerUrl}
                    defaultCategoryName={categories?.[0]?.value ?? 'generic'}
                    loading={
                        !listingData ||
                        profileInfoLoading ||
                        listingDataLoading ||
                        !profileInfoData?.firm ||
                        !!profileInfoError
                    }
                />
            )}
            <GridContainer
                justifyContent="space-between"
                spacing={4}
                className={clsx(classes.page, classes.content)}
            >
                <GridItem
                    container
                    direction="column"
                    xs={12}
                    md={7}
                    spacing={2}
                    className={clsx(
                        mdDown && classes.noRightPadding,
                        classes.leftColumnContainer,
                        !mdDown && classes.limitedHeight
                    )}
                >
                    <GridItem>
                        {isEditMode ? (
                            <CompanyLogoEdit
                                logoUrl={
                                    profileInfoData?.firm.profileInfo?.logoUrl
                                }
                                companyLogoFileInTransit={
                                    companyLogoFileInTransit
                                }
                                handleCompanyLogoFileInTransit={
                                    handleCompanyLogoFileInTransitChange
                                }
                                handleCompanyLogoChange={handleLogoImageChange}
                                handleFilestackLoading={handleFilestackLoading}
                            />
                        ) : (
                            <CompanyLogo
                                loading={
                                    !profileInfoData ||
                                    profileInfoLoading ||
                                    !!profileInfoError
                                }
                                logoUrl={
                                    profileInfoData?.firm?.profileInfo?.logoUrl
                                }
                            />
                        )}
                    </GridItem>
                    <GridItem
                        container
                        direction="column"
                        className={classes.fullWidth}
                    >
                        <GridItem className={classes.fullWidth}>
                            <EditableText
                                editable={isEditMode && isDeltekAdmin}
                                firmLoading={profileInfoLoading}
                                data-testid="companyNameEditor"
                                textContent={
                                    textFieldContents.displayName.value ?? ''
                                }
                                onBlur={handleDisplayNameChange}
                            />
                        </GridItem>
                        <GridItem>
                            <CompanyContactInfo
                                firmLoading={
                                    profileInfoLoading || !!profileInfoError
                                }
                                address={
                                    profileInfoData?.firm?.profileInfo?.address
                                }
                                website={
                                    profileInfoData?.firm?.profileInfo
                                        ?.website ?? ''
                                }
                                publicPhone={
                                    profileInfoData?.firm?.profileInfo
                                        ?.publicPhone ?? ''
                                }
                            />
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.fullWidth,
                                classes.dividerContainer
                            )}
                        >
                            <Divider className={classes.thickDivider} />
                        </GridItem>
                    </GridItem>
                    <GridItem
                        container
                        direction="column"
                        style={{
                            maxHeight: mdDown ? 300 : 'unset',
                        }}
                        className={clsx(classes.fullWidth, classes.textBlocks)}
                        spacing={2}
                    >
                        <GridItem>
                            <EditableTextBlock
                                data-testid="whoWeAreTextBlock"
                                title="Who We Are"
                                editable={isEditMode}
                                maxChars={10000}
                                content={textFieldContents.whoWeAre.value ?? ''}
                                isLoading={
                                    profileInfoLoading || !!profileInfoError
                                }
                                valid={!!textFieldContents.whoWeAre.valid}
                                onChange={(newContent) =>
                                    handleTextFieldContentChange(
                                        newContent.content,
                                        'whoWeAre'
                                    )
                                }
                            />
                        </GridItem>
                    </GridItem>
                </GridItem>
                <GridContainer
                    item
                    xs={12}
                    md={5}
                    spacing={2}
                    direction="column"
                    className={clsx(
                        mdDown && classes.noRightPadding,
                        classes.leftColumnContainer,
                        !mdDown && classes.limitedHeight
                    )}
                >
                    {chunkArray(listingTypes, 2).map((listingRow) => (
                        <GridContainer
                            justifyContent="flex-start"
                            alignContent="stretch"
                            item
                            spacing={2}
                        >
                            {listingRow.map((listing) => {
                                let listingName =
                                    listing === ListingLevel.PremiumProprietary
                                        ? 'Proprietary'
                                        : listing
                                return (
                                    <GridItem sm={6} xs={12}>
                                        <ListingTypeWidget
                                            data-testid={`${listingName
                                                .charAt(0)
                                                .toLowerCase()}${listingName
                                                    .slice(1)
                                                    .replace(/\s/g, '')}${listingName !==
                                                        ListingLevel.ProductListing
                                                        ? 'Listing'
                                                        : ''
                                                }CountWidget`}
                                            loading={
                                                listingDataLoading ||
                                                profileInfoLoading
                                            }
                                            error={listingDataError}
                                            count={getListingLevelCount(
                                                listing as ListingLevel
                                            )}
                                            infoBubble={dummyInfoBubble}
                                            listingName={`${listingName}${listingName !==
                                                ListingLevel.ProductListing
                                                ? ' Listings'
                                                : 's'
                                                }`}
                                            headerMinHeight={headerMinHeight}
                                            firmName={
                                                profileInfoData?.firm?.name
                                            }
                                            firmDisplayName={
                                                profileInfoData?.firm
                                                    ?.displayName
                                            }
                                            listingTypeFilters={[
                                                listing as ListingLevel,
                                            ]}
                                            callingFromAE
                                        />
                                    </GridItem>
                                )
                            })}
                        </GridContainer>
                    ))}
                    <GridContainer justifyContent="flex-end" item spacing={2}>
                        <GridItem xs={12}>
                            <ListingCategoriesWidget
                                categories={categories}
                                firmLoading={
                                    listingDataLoading || !!listingDataError
                                }
                                contentHeight={
                                    categories.length > 4
                                        ? window.innerHeight - 677
                                        : 180
                                }
                                firmName={profileInfoData?.firm?.name}
                                firmDisplayName={
                                    profileInfoData?.firm?.displayName
                                }
                                error={listingDataError}
                                callingFromAE
                            />
                        </GridItem>
                    </GridContainer>
                </GridContainer>
            </GridContainer>
            <Backdrop
                className={classes.backdrop}
                open={!!updateFirmDisplayInfoLoading || filestackLoading}
            >
                {!filestackLoading && <CircularProgress color="inherit" />}
            </Backdrop>
            <NavPrompt showPrompt={isFormDirty} title="Company Page" />
        </div>
    )
}

export default CompanyPage
